import type { IconName } from "./icon-names";

/**
 * CDS catalog ligature → Phosphor kebab name.
 *
 * Catalog `name` stays the product vocabulary (Material ligatures). Phosphor is
 * a renderer, not a second naming scheme. Pin/preferred is still `keep`.
 *
 * Source of Phosphor names: `@phosphor-icons/core` (github.com/phosphor-icons/core).
 */
export const MATERIAL_TO_PHOSPHOR = {
  accessibility_new: "person-arms-spread",
  account_tree: "tree-structure",
  add: "plus",
  add_comment: "chat-circle-text",
  ads_click: "cursor-click",
  agriculture: "plant",
  alternate_email: "at",
  anchor: "anchor",
  apartment: "buildings",
  arrow_back: "arrow-left",
  arrow_downward: "arrow-down",
  arrow_forward: "arrow-right",
  arrow_upward: "arrow-up",
  article: "article",
  auto_awesome: "sparkle",
  auto_fix_high: "magic-wand",
  bar_chart: "chart-bar",
  bolt: "lightning",
  brush: "paint-brush",
  build: "wrench",
  calculate: "calculator",
  calendar_today: "calendar-blank",
  call: "phone",
  cancel: "x-circle",
  category: "shapes",
  chair: "armchair",
  chat_bubble: "chat-circle",
  check: "check",
  check_box: "check-square",
  check_circle: "check-circle",
  checkroom: "t-shirt",
  chevron_left: "caret-left",
  chevron_right: "caret-right",
  circle: "circle",
  close: "x",
  close_fullscreen: "arrows-in",
  code: "code",
  content_copy: "copy",
  cooking: "cooking-pot",
  credit_card: "credit-card",
  crop: "crop",
  crop_square: "square",
  dark_mode: "moon",
  dashboard: "squares-four",
  data_object: "brackets-curly",
  database: "database",
  delete: "trash",
  description: "file-text",
  deployed_code: "cube",
  developer_board: "cpu",
  difference: "git-diff",
  directions_car: "car",
  done_all: "checks",
  download: "download-simple",
  drag_indicator: "dots-six-vertical",
  edit: "pencil-simple",
  edit_note: "note-pencil",
  error: "warning-circle",
  expand_less: "caret-up",
  expand_more: "caret-down",
  extension: "puzzle-piece",
  factory: "factory",
  file_copy: "copy-simple",
  filter_alt: "funnel",
  filter_list: "funnel-simple",
  flag: "flag",
  flip: "flip-horizontal",
  format_paint: "paint-bucket",
  function: "function",
  grid_view: "squares-four",
  group: "users",
  home: "house",
  image: "image",
  info: "info",
  inventory_2: "package",
  keep: "push-pin",
  keep_off: "push-pin-slash",
  keyboard_double_arrow_left: "caret-double-left",
  keyboard_double_arrow_right: "caret-double-right",
  label: "tag",
  layers: "stack",
  left_panel_close: "sidebar-simple",
  light_mode: "sun",
  link: "link",
  list: "list",
  local_library: "books",
  local_shipping: "truck",
  location_on: "map-pin",
  lock: "lock",
  logout: "sign-out",
  mail: "envelope",
  memory: "memory",
  menu: "list",
  merge: "git-merge",
  more_horiz: "dots-three",
  more_vert: "dots-three-vertical",
  north_east: "arrow-up-right",
  notifications: "bell",
  notifications_off: "bell-slash",
  open_in_full: "arrows-out",
  open_in_new: "arrow-square-out",
  package_2: "package",
  palette: "palette",
  pause: "pause",
  pending: "circle-dashed",
  pie_chart: "chart-pie",
  play_arrow: "play",
  power: "power",
  print: "printer",
  progress_activity: "circle-notch",
  receipt: "receipt",
  redo: "arrow-clockwise",
  refresh: "arrows-clockwise",
  remove: "minus",
  repeat: "repeat",
  restaurant: "fork-knife",
  right_panel_open: "sidebar",
  save: "floppy-disk",
  scatter_plot: "chart-scatter",
  schedule: "clock",
  science: "flask",
  search: "magnifying-glass",
  sell: "tag",
  send: "paper-plane-tilt",
  sentiment_satisfied: "smiley",
  settings: "gear",
  share: "share-network",
  shield: "shield",
  show_chart: "chart-line",
  smart_toy: "robot",
  smartphone: "device-mobile",
  star: "star",
  sticky_note_2: "note",
  swap_vert: "arrows-down-up",
  table: "table",
  table_rows: "rows",
  tonality: "circle-half",
  trending_up: "trend-up",
  tune: "faders",
  undo: "arrow-counter-clockwise",
  unfold_less: "arrows-in-line-vertical",
  unfold_more: "arrows-out-line-vertical",
  upload: "upload-simple",
  verified_user: "shield-check",
  vertical_align_bottom: "arrow-line-down",
  vertical_align_top: "arrow-line-up",
  view_column: "columns",
  view_list: "list-bullets",
  view_week: "columns",
  visibility: "eye",
  visibility_off: "eye-slash",
  warning: "warning",
  water_drop: "drop",
  widgets: "grid-four",
} as const satisfies Record<IconName, string>;

export type PhosphorIconName = (typeof MATERIAL_TO_PHOSPHOR)[IconName];

/** Ligatures used outside the catalog (Storybook / docs escape hatch). */
export const PHOSPHOR_ESCAPE: Record<string, PhosphorIconName | (string & {})> = {
  email: "envelope",
  play_circle: "play-circle",
  web: "globe",
};

export function phosphorNameFor(ligature: string): string | undefined {
  if (ligature in MATERIAL_TO_PHOSPHOR) {
    return MATERIAL_TO_PHOSPHOR[ligature as IconName];
  }
  return PHOSPHOR_ESCAPE[ligature];
}
